import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { providersApi, sitesApi } from '@/services/config';

import styles from './ConfigSeo.module.css';

// Espelho da regra do backend (app/services/slug_rule.py) SÓ para o preview ao
// vivo — quem manda é o backend na publicação. Se as listas divergirem, o
// preview erra por detalhe; a URL publicada segue a regra do servidor.
const STOPWORDS = new Set(
  (
    'a o as os um uma uns umas de do da dos das em no na nos nas por para pra ' +
    'com sem sob sobre e ou que se ao aos à às é são foi será está estão tem ' +
    'têm vai vão como quando onde confira veja saiba entenda'
  ).split(' '),
);

function buildSlug(title: string, maxLen: number): string {
  const words = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  let kept = words.filter((w) => !STOPWORDS.has(w));
  if (kept.length === 0) kept = words;
  const out: string[] = [];
  let size = 0;
  for (const w of kept) {
    const add = w.length + (out.length ? 1 : 0);
    if (size + add > maxLen) break;
    out.push(w);
    size += add;
  }
  if (out.length === 0) out.push(kept[0].slice(0, maxLen));
  return out.join('-');
}

const EXEMPLO = 'Confira o resultado do sorteio da Mega-Sena concurso 2953';

export function ConfigSeo() {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(true);
  const [maxLen, setMaxLen] = useState(60);
  const [exemplo, setExemplo] = useState(EXEMPLO);
  const [msg, setMsg] = useState<string | null>(null);

  const cfgQ = useQuery({
    queryKey: ['config', 'providers', 'seo_slug'],
    queryFn: async () => {
      try {
        return await providersApi.get('seo_slug');
      } catch {
        return null; // 404 = nunca configurado → defaults
      }
    },
    retry: false,
  });

  useEffect(() => {
    const c = cfgQ.data?.config as { enabled?: boolean; max_len?: number } | undefined;
    if (c) {
      if (typeof c.enabled === 'boolean') setEnabled(c.enabled);
      if (typeof c.max_len === 'number') setMaxLen(c.max_len);
    }
  }, [cfgQ.data]);

  const save = useMutation({
    mutationFn: () => providersApi.upsert('seo_slug', { enabled, max_len: maxLen }),
    onSuccess: () => {
      setMsg('Salvo. Vale para as próximas publicações — nunca reescreve URL já publicada.');
      qc.invalidateQueries({ queryKey: ['config', 'providers', 'seo_slug'] });
    },
    onError: (e) => setMsg(`Erro ao salvar: ${String(e)}`),
  });

  const preview = useMemo(
    () => (enabled ? buildSlug(exemplo, maxLen) : '(desligado — o WordPress copia o título)'),
    [enabled, exemplo, maxLen],
  );

  return (
    <section className={styles.root}>
      <h1 className={styles.title}>SEO — Endereço da matéria (URL)</h1>
      <p className={styles.subtitle}>
        Regra global do slug enviado ao WordPress na publicação: minúsculo, sem acento, sem palavra
        vazia, cortado em palavra inteira. Vale só para o que nasce daqui em diante — URL publicada
        nunca é reescrita.
      </p>

      <div className={styles.card}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Regra ligada (desligada = comportamento antigo: o WordPress copia o título)
        </label>

        <label style={{ display: 'block', marginTop: 'var(--sp-4)' }}>
          Limite de caracteres: <strong>{maxLen}</strong>
          <input
            type="range"
            min={30}
            max={100}
            value={maxLen}
            onChange={(e) => setMaxLen(Number(e.target.value))}
            style={{ width: '100%', display: 'block' }}
          />
        </label>

        <label style={{ display: 'block', marginTop: 'var(--sp-4)' }}>
          Teste com um título:
          <input
            type="text"
            value={exemplo}
            onChange={(e) => setExemplo(e.target.value)}
            style={{ width: '100%', display: 'block', marginTop: 'var(--sp-1)' }}
          />
        </label>

        <p style={{ marginTop: 'var(--sp-3)' }}>
          URL prevista:{' '}
          <code>
            /{preview}
            {enabled && preview ? '/' : ''}
          </code>
        </p>

        <div style={{ marginTop: 'var(--sp-4)' }}>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
        {msg && <p style={{ marginTop: 'var(--sp-2)' }}>{msg}</p>}
      </div>

      <SufixoDoTema />
    </section>
  );
}

/**
 * Sufixo que o tema do site cola no fim de todo título antes de mandar ao
 * Google. Medido em um site de referência em 08/08/2026: sufixo de marca de 10 caracteres. Numa
 * amostra de 20 matérias, 18 estouravam o teto de 70 por causa dele — e ninguém
 * via, porque na tela do editor o título parecia curto.
 *
 * Declarar aqui faz a régua do editor medir o título SERVIDO. Não muda o site:
 * quem tira o sufixo de verdade é o painel do tema (Rank Math), e tirar lá
 * recupera os 10 caracteres em TODO o acervo, de uma vez.
 */
function SufixoDoTema() {
  const qc = useQueryClient();
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const sitesQ = useQuery({
    queryKey: ['config', 'sites'],
    queryFn: () => sitesApi.list(),
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: ({ id, field_map }: { id: string; field_map: Record<string, string> }) =>
      sitesApi.update(id, { field_map }),
    onSuccess: () => {
      setAviso('Salvo. A régua do editor passa a contar com o sufixo.');
      qc.invalidateQueries({ queryKey: ['config', 'sites'] });
    },
    onError: (e) => setAviso(`Erro ao salvar: ${String(e)}`),
  });

  const sites = sitesQ.data ?? [];

  return (
    <div className={styles.card} style={{ marginTop: 'var(--sp-4)' }}>
      <h2 style={{ marginTop: 0 }}>Sufixo do tema no título</h2>
      <p>
        O tema acrescenta um texto ao fim de todo título antes de enviar ao Google (por exemplo{' '}
        <code> - NC News</code>). Esses caracteres contam para o limite de 70 e são invisíveis para
        quem escreve. Declare o sufixo aqui para a régua do editor medir o título de verdade.
      </p>

      {sites.length === 0 && <p>Nenhum site configurado.</p>}

      {sites.map((s) => {
        const atual = rascunho[s.id] ?? s.field_map?.titulo_sufixo ?? '';
        const mudou = atual !== (s.field_map?.titulo_sufixo ?? '');
        return (
          <div key={s.id} style={{ marginTop: 'var(--sp-3)' }}>
            <label style={{ display: 'block' }}>
              {s.name} <code>({s.slug})</code>
              <input
                type="text"
                value={atual}
                placeholder=" - Nome do site"
                onChange={(e) => setRascunho((r) => ({ ...r, [s.id]: e.target.value }))}
                style={{ width: '100%', display: 'block', marginTop: 'var(--sp-1)' }}
              />
            </label>
            <p style={{ fontSize: 12, marginTop: 'var(--sp-1)' }}>
              {atual
                ? `Sobram ${Math.max(0, 70 - atual.length)} caracteres para a manchete.`
                : 'Sem sufixo declarado: a régua conta 70 cheios.'}
            </p>
            {mudou && (
              <Button
                size="sm"
                onClick={() =>
                  salvar.mutate({
                    id: s.id,
                    field_map: { ...(s.field_map ?? {}), titulo_sufixo: atual },
                  })
                }
                disabled={salvar.isPending}
              >
                {salvar.isPending ? 'Salvando…' : `Salvar ${s.slug}`}
              </Button>
            )}
          </div>
        );
      })}

      {aviso && <p style={{ marginTop: 'var(--sp-2)' }}>{aviso}</p>}
    </div>
  );
}
